import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { Button, Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@mono/ui-components';
import { Testids } from '@mono/ui-selectors';
import { Download, Eye, File, FileText, Image, Loader2, MoreVertical, RefreshCw, Search, Sparkles, Trash2, Upload } from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import ArtifactsView from '../../components/ArtifactsView/ArtifactsView';
import { ArtifactEditorDrawer } from '../../components/artifacts/ArtifactEditorDrawer';
import { OfficeDocumentDrawer } from '../../components/ChatView/OfficeDocumentDrawer';
import { DocumentPreviewModal } from '../../components/DocumentPreviewModal';
import { PageBody, PageHero } from '../../components/layout';
import { ArtifactDrawerProvider, useArtifactDrawer } from '../../contexts/ArtifactDrawerContext';
import { useFeatureFlag, useFeatureFlagsReady } from '../../contexts/featureFlagContext';
import { OfficeDocumentDrawerProvider } from '../../contexts/OfficeDocumentDrawerContext';
import { useDocumentSearch } from '../../hooks/useDocumentSearch';
import { useDocuments } from '../../hooks/useDocuments';
import { useProjects } from '../../hooks/useProjects';
import { documentApiConnect } from '../../lib/api/documentApi.connect';
import { useLayoutStore } from '../../lib/stores/LayoutStore';
const getFileIcon = (contentType)=>{
    if (contentType.includes('pdf')) {
        return /*#__PURE__*/ _jsx(FileText, {
            className: "w-6 h-6 text-red-500"
        });
    }
    if (contentType.includes('word') || contentType.includes('document')) {
        return /*#__PURE__*/ _jsx(FileText, {
            className: "w-6 h-6 text-blue-500"
        });
    }
    if (contentType.includes('presentation') || contentType.includes('powerpoint')) {
        return /*#__PURE__*/ _jsx(FileText, {
            className: "w-6 h-6 text-orange-500"
        });
    }
    if (contentType.includes('text/plain')) {
        return /*#__PURE__*/ _jsx(FileText, {
            className: "w-6 h-6 text-gray-500"
        });
    }
    if (contentType.includes('image')) {
        return /*#__PURE__*/ _jsx(Image, {
            className: "w-6 h-6 text-green-500"
        });
    }
    return /*#__PURE__*/ _jsx(File, {
        className: "w-6 h-6 text-gray-500"
    });
};
const formatFileSize = (bytes)=>{
    const sizes = [
        'Bytes',
        'KB',
        'MB',
        'GB'
    ];
    if (bytes === 0) return '0 Bytes';
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return `${Math.round(bytes / 1024 ** i * 100) / 100} ${sizes[i]}`;
};
const formatDate = (dateString)=>{
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString([], {
        hour: '2-digit',
        minute: '2-digit'
    });
};
const getStatusBadgeClass = (status)=>{
    switch(status){
        case 'ready':
            return 'bg-green-100 text-green-800 dark:bg-green-900/20 dark:text-green-400';
        case 'processing':
        case 'queued':
            return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-400';
        case 'error':
            return 'bg-red-100 text-red-800 dark:bg-red-900/20 dark:text-red-400';
        default:
            return 'bg-gray-100 text-gray-800 dark:bg-gray-900/20 dark:text-gray-400';
    }
};
const getStatusLabel = (status)=>{
    switch(status){
        case 'none':
            return 'Pending';
        case 'queued':
            return 'Queued';
        case 'processing':
            return 'Processing';
        case 'ready':
            return 'Ready';
        case 'error':
            return 'Error';
        default:
            return status;
    }
};
function DocumentsContent() {
    const { isOpen, closeDrawer } = useArtifactDrawer();
    const [activeTab, setActiveTab] = useState('documents');
    const [searchMode, setSearchMode] = useState('filename');
    const [searchQuery, setSearchQuery] = useState('');
    const [semanticQuery, setSemanticQuery] = useState('');
    const [filterStatus, setFilterStatus] = useState('all');
    const [sortBy, setSortBy] = useState('recent');
    const [selectedDocuments, setSelectedDocuments] = useState([]);
    const [viewMode, _setViewMode] = useState('list');
    const [previewDocument, setPreviewDocument] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [isDeleting, setIsDeleting] = useState(false);
    // Resolve project ids on documents to names so a document processed under a
    // project is filed under it here (not shown as an unplaced, general doc).
    const { data: projectsData } = useProjects();
    const projectNameById = useMemo(()=>new Map((projectsData?.projects ?? []).map((p)=>[
                p.projectId,
                p.name
            ])), [
        projectsData
    ]);
    // RAG semantic search
    const { search: performSemanticSearch, data: semanticResults, isSearching, error: searchError } = useDocumentSearch();
    // Fetch documents with auto-refresh for in-progress items
    const { documents: allDocuments, loading, error: fetchError, total, refetch } = useDocuments({
        sortBy,
        status: filterStatus === 'all' ? undefined : filterStatus,
        autoRefresh: true,
        refreshInterval: 5000
    });
    // Handle semantic search
    const handleSemanticSearch = ()=>{
        if (!semanticQuery.trim()) return;
        performSemanticSearch({
            query: semanticQuery,
            top_k: 20
        });
    };
    // Determine which documents to display based on search mode
    const displayDocuments = searchMode === 'semantic' && semanticResults ? semanticResults.documents.map((doc)=>({
            id: doc.id,
            filename: doc.title || 'Untitled',
            contentType: doc.source_type || 'application/octet-stream',
            size: 0,
            createdAt: new Date().toISOString(),
            status: 'ready',
            relevanceScore: doc.relevance_score,
            content: doc.content
        })) : allDocuments;
    // Client-side search filtering for filename mode
    const filteredDocuments = searchMode === 'filename' ? displayDocuments.filter((doc)=>{
        if (!searchQuery) return true;
        return doc.filename.toLowerCase().includes(searchQuery.toLowerCase());
    }) : displayDocuments;
    const handleSelectDocument = (docId)=>{
        setSelectedDocuments((prev)=>prev.includes(docId) ? prev.filter((id)=>id !== docId) : [
                ...prev,
                docId
            ]);
    };
    const handleSelectAll = ()=>{
        if (selectedDocuments.length === filteredDocuments.length) {
            setSelectedDocuments([]);
        } else {
            setSelectedDocuments(filteredDocuments.map((doc)=>doc.id));
        }
    };
    const handleDownload = async (doc)=>{
        if (doc.status !== 'ready') {
            alert('Document is not ready for download');
            return;
        }
        try {
            const result = await documentApiConnect.downloadDocument(doc.id);
            const url = window.URL.createObjectURL(result.blob);
            const link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', result.filename);
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error('Download error:', err);
            alert(`Failed to download document: ${err instanceof Error ? err.message : 'Unknown error'}`);
        }
    };
    const handlePreview = (doc)=>{
        if (doc.status !== 'ready') {
            toast.warning('Document is not ready for preview');
            return;
        }
        setPreviewDocument(doc);
    };
    const handleDeleteConfirm = async ()=>{
        if (!deleteTarget) return;
        setIsDeleting(true);
        try {
            const result = await documentApiConnect.deleteDocuments(deleteTarget.ids);
            toast.success(`Deleted ${result.deleted} document(s)`);
            setSelectedDocuments((prev)=>prev.filter((id)=>!deleteTarget.ids.includes(id)));
            refetch();
        } catch (err) {
            toast.error(err instanceof Error ? err.message : 'Delete failed');
        } finally{
            setIsDeleting(false);
            setDeleteTarget(null);
        }
    };
    const DocumentCard = ({ document: document1 })=>/*#__PURE__*/ _jsx("div", {
            className: "bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm hover:shadow-md transition-shadow",
            children: /*#__PURE__*/ _jsxs("div", {
                className: "p-4",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex items-start justify-between mb-3",
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                className: "flex items-center space-x-3",
                                children: [
                                    /*#__PURE__*/ _jsx("input", {
                                        type: "checkbox",
                                        checked: selectedDocuments.includes(document1.id),
                                        onChange: ()=>handleSelectDocument(document1.id),
                                        className: "rounded border-gray-300 text-indigo-600 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                                    }),
                                    getFileIcon(document1.contentType),
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "min-w-0 flex-1",
                                        children: [
                                            /*#__PURE__*/ _jsx("h3", {
                                                className: "text-sm font-medium text-gray-900 dark:text-white truncate",
                                                children: document1.filename
                                            }),
                                            /*#__PURE__*/ _jsxs("div", {
                                                className: "flex items-center space-x-4 mt-1 text-xs text-gray-500 dark:text-gray-400",
                                                children: [
                                                    /*#__PURE__*/ _jsx("span", {
                                                        children: formatFileSize(document1.size)
                                                    }),
                                                    /*#__PURE__*/ _jsx("span", {
                                                        children: formatDate(document1.createdAt)
                                                    })
                                                ]
                                            })
                                        ]
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "flex items-center space-x-1",
                                children: [
                                    /*#__PURE__*/ _jsx("span", {
                                        className: `inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusBadgeClass(document1.status)}`,
                                        children: getStatusLabel(document1.status)
                                    }),
                                    /*#__PURE__*/ _jsxs(DropdownMenu, {
                                        children: [
                                            /*#__PURE__*/ _jsx(DropdownMenuTrigger, {
                                                asChild: true,
                                                children: /*#__PURE__*/ _jsx("button", {
                                                    className: "p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300",
                                                    children: /*#__PURE__*/ _jsx(MoreVertical, {
                                                        className: "w-4 h-4"
                                                    })
                                                })
                                            }),
                                            /*#__PURE__*/ _jsxs(DropdownMenuContent, {
                                                align: "end",
                                                children: [
                                                    /*#__PURE__*/ _jsxs(DropdownMenuItem, {
                                                        disabled: document1.status !== 'ready',
                                                        onClick: ()=>handleDownload(document1),
                                                        children: [
                                                            /*#__PURE__*/ _jsx(Download, {
                                                                className: "w-4 h-4 mr-2"
                                                            }),
                                                            "Download"
                                                        ]
                                                    }),
                                                    /*#__PURE__*/ _jsxs(DropdownMenuItem, {
                                                        disabled: document1.status !== 'ready',
                                                        onClick: ()=>handlePreview(document1),
                                                        children: [
                                                            /*#__PURE__*/ _jsx(Eye, {
                                                                className: "w-4 h-4 mr-2"
                                                            }),
                                                            "Preview"
                                                        ]
                                                    }),
                                                    /*#__PURE__*/ _jsxs(DropdownMenuItem, {
                                                        className: "text-red-600 dark:text-red-400",
                                                        onClick: ()=>setDeleteTarget({
                                                                ids: [
                                                                    document1.id
                                                                ],
                                                                label: document1.filename
                                                            }),
                                                        children: [
                                                            /*#__PURE__*/ _jsx(Trash2, {
                                                                className: "w-4 h-4 mr-2"
                                                            }),
                                                            "Delete"
                                                        ]
                                                    })
                                                ]
                                            })
                                        ]
                                    })
                                ]
                            })
                        ]
                    }),
                    document1.progress && /*#__PURE__*/ _jsxs("div", {
                        className: "mb-3",
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                className: "flex justify-between text-xs text-gray-600 dark:text-gray-400 mb-1",
                                children: [
                                    /*#__PURE__*/ _jsx("span", {
                                        children: document1.progress.currentStep || 'Processing...'
                                    }),
                                    /*#__PURE__*/ _jsxs("span", {
                                        children: [
                                            document1.progress.percentage,
                                            "%"
                                        ]
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                className: "w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5",
                                children: /*#__PURE__*/ _jsx("div", {
                                    className: "bg-indigo-600 h-1.5 rounded-full transition-all",
                                    style: {
                                        width: `${document1.progress.percentage || 0}%`
                                    }
                                })
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex items-center justify-between",
                        children: [
                            /*#__PURE__*/ _jsx("div", {
                                className: "text-xs text-gray-500 dark:text-gray-400",
                                children: /*#__PURE__*/ _jsxs("span", {
                                    className: "font-mono truncate",
                                    children: [
                                        document1.id.slice(0, 8),
                                        "..."
                                    ]
                                })
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "flex items-center space-x-1",
                                children: [
                                    /*#__PURE__*/ _jsx("button", {
                                        onClick: ()=>handleDownload(document1),
                                        disabled: document1.status !== 'ready',
                                        className: "p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed",
                                        title: "Download",
                                        children: /*#__PURE__*/ _jsx(Download, {
                                            className: "w-4 h-4"
                                        })
                                    }),
                                    /*#__PURE__*/ _jsx("button", {
                                        onClick: ()=>handlePreview(document1),
                                        disabled: document1.status !== 'ready',
                                        className: "p-1 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 disabled:opacity-50 disabled:cursor-not-allowed",
                                        title: "Preview",
                                        children: /*#__PURE__*/ _jsx(Eye, {
                                            className: "w-4 h-4"
                                        })
                                    })
                                ]
                            })
                        ]
                    }),
                    document1.error && /*#__PURE__*/ _jsx("div", {
                        className: "mt-3 p-2 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded text-xs text-red-700 dark:text-red-300",
                        children: document1.error
                    })
                ]
            })
        });
    const DocumentRow = ({ document: document1 })=>{
        const docWithRelevance = document1;
        return /*#__PURE__*/ _jsxs("tr", {
            className: "hover:bg-gray-50 dark:hover:bg-gray-700/50",
            children: [
                /*#__PURE__*/ _jsx("td", {
                    className: "px-6 py-4 whitespace-nowrap",
                    children: /*#__PURE__*/ _jsx("input", {
                        type: "checkbox",
                        checked: selectedDocuments.includes(document1.id),
                        onChange: ()=>handleSelectDocument(document1.id),
                        className: "rounded border-gray-300 text-indigo-600 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                    })
                }),
                /*#__PURE__*/ _jsx("td", {
                    className: "px-6 py-4",
                    children: /*#__PURE__*/ _jsxs("div", {
                        className: "flex items-center min-w-[200px]",
                        children: [
                            getFileIcon(document1.contentType),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "ml-3",
                                children: [
                                    /*#__PURE__*/ _jsx("div", {
                                        className: "text-sm font-medium text-gray-900 dark:text-white",
                                        children: document1.filename
                                    }),
                                    searchMode === 'filename' && /*#__PURE__*/ _jsx("div", {
                                        className: "text-sm text-gray-500 dark:text-gray-400",
                                        children: formatFileSize(document1.size)
                                    })
                                ]
                            })
                        ]
                    })
                }),
                searchMode === 'semantic' && docWithRelevance.content && /*#__PURE__*/ _jsx("td", {
                    className: "px-6 py-4 max-w-md",
                    children: /*#__PURE__*/ _jsx("div", {
                        className: "text-xs text-gray-600 dark:text-gray-400 line-clamp-3",
                        children: docWithRelevance.content
                    })
                }),
                searchMode === 'filename' && /*#__PURE__*/ _jsx("td", {
                    className: "px-6 py-4 whitespace-nowrap",
                    children: document1.projectId ? /*#__PURE__*/ _jsx(Link, {
                        to: `/project/${document1.projectId}`,
                        className: "text-sm text-indigo-600 hover:text-indigo-800 dark:text-indigo-400 dark:hover:text-indigo-300",
                        children: projectNameById.get(document1.projectId) ?? document1.projectId
                    }) : /*#__PURE__*/ _jsx("span", {
                        className: "text-sm text-gray-400 dark:text-gray-500",
                        children: "Standalone"
                    })
                }),
                /*#__PURE__*/ _jsx("td", {
                    className: "px-6 py-4 whitespace-nowrap",
                    children: searchMode === 'semantic' && docWithRelevance.relevanceScore !== undefined ? /*#__PURE__*/ _jsxs("div", {
                        className: "flex items-center min-w-[120px]",
                        children: [
                            /*#__PURE__*/ _jsx("div", {
                                className: "w-24 bg-gray-200 dark:bg-gray-700 rounded-full h-2 mr-2",
                                children: /*#__PURE__*/ _jsx("div", {
                                    className: "bg-indigo-600 h-2 rounded-full",
                                    style: {
                                        width: `${docWithRelevance.relevanceScore * 100}%`
                                    }
                                })
                            }),
                            /*#__PURE__*/ _jsxs("span", {
                                className: "text-xs font-medium text-gray-700 dark:text-gray-300",
                                children: [
                                    (docWithRelevance.relevanceScore * 100).toFixed(1),
                                    "%"
                                ]
                            })
                        ]
                    }) : /*#__PURE__*/ _jsxs("div", {
                        children: [
                            /*#__PURE__*/ _jsx("span", {
                                className: `inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${getStatusBadgeClass(document1.status)}`,
                                children: getStatusLabel(document1.status)
                            }),
                            document1.progress && /*#__PURE__*/ _jsx("div", {
                                className: "mt-1 w-32",
                                children: /*#__PURE__*/ _jsx("div", {
                                    className: "w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1",
                                    children: /*#__PURE__*/ _jsx("div", {
                                        className: "bg-indigo-600 h-1 rounded-full transition-all",
                                        style: {
                                            width: `${document1.progress.percentage || 0}%`
                                        }
                                    })
                                })
                            })
                        ]
                    })
                }),
                /*#__PURE__*/ _jsx("td", {
                    className: "px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400",
                    children: searchMode === 'semantic' ? semanticResults?.documents.find((d)=>d.id === document1.id)?.source || 'Unknown' : formatDate(document1.createdAt)
                }),
                /*#__PURE__*/ _jsx("td", {
                    className: "px-6 py-4 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400",
                    children: searchMode === 'semantic' ? semanticResults?.documents.find((d)=>d.id === document1.id)?.source_type || 'N/A' : /*#__PURE__*/ _jsxs("span", {
                        className: "font-mono text-xs",
                        children: [
                            document1.id.slice(0, 12),
                            "..."
                        ]
                    })
                }),
                /*#__PURE__*/ _jsx("td", {
                    className: "px-6 py-4 whitespace-nowrap text-right text-sm font-medium",
                    children: /*#__PURE__*/ _jsx("div", {
                        className: "flex items-center justify-end space-x-2",
                        children: searchMode === 'semantic' ? /*#__PURE__*/ _jsx("span", {
                            className: "text-xs text-gray-400 dark:text-gray-500 italic",
                            children: "Text chunk"
                        }) : /*#__PURE__*/ _jsxs(_Fragment, {
                            children: [
                                /*#__PURE__*/ _jsx("button", {
                                    onClick: ()=>handleDownload(document1),
                                    disabled: document1.status !== 'ready',
                                    className: "text-indigo-600 hover:text-indigo-900 dark:text-indigo-400 dark:hover:text-indigo-300 disabled:opacity-50 disabled:cursor-not-allowed",
                                    title: "Download",
                                    children: /*#__PURE__*/ _jsx(Download, {
                                        className: "w-4 h-4"
                                    })
                                }),
                                /*#__PURE__*/ _jsx("button", {
                                    onClick: ()=>handlePreview(document1),
                                    disabled: document1.status !== 'ready',
                                    className: "text-indigo-600 hover:text-indigo-900 dark:text-indigo-400 dark:hover:text-indigo-300 disabled:opacity-50 disabled:cursor-not-allowed",
                                    title: "Preview",
                                    children: /*#__PURE__*/ _jsx(Eye, {
                                        className: "w-4 h-4"
                                    })
                                }),
                                /*#__PURE__*/ _jsxs(DropdownMenu, {
                                    children: [
                                        /*#__PURE__*/ _jsx(DropdownMenuTrigger, {
                                            asChild: true,
                                            children: /*#__PURE__*/ _jsx("button", {
                                                className: "text-gray-400 hover:text-gray-600 dark:hover:text-gray-300",
                                                children: /*#__PURE__*/ _jsx(MoreVertical, {
                                                    className: "w-4 h-4"
                                                })
                                            })
                                        }),
                                        /*#__PURE__*/ _jsxs(DropdownMenuContent, {
                                            align: "end",
                                            children: [
                                                /*#__PURE__*/ _jsxs(DropdownMenuItem, {
                                                    disabled: document1.status !== 'ready',
                                                    onClick: ()=>handleDownload(document1),
                                                    children: [
                                                        /*#__PURE__*/ _jsx(Download, {
                                                            className: "w-4 h-4 mr-2"
                                                        }),
                                                        "Download"
                                                    ]
                                                }),
                                                /*#__PURE__*/ _jsxs(DropdownMenuItem, {
                                                    disabled: document1.status !== 'ready',
                                                    onClick: ()=>handlePreview(document1),
                                                    children: [
                                                        /*#__PURE__*/ _jsx(Eye, {
                                                            className: "w-4 h-4 mr-2"
                                                        }),
                                                        "Preview"
                                                    ]
                                                }),
                                                /*#__PURE__*/ _jsxs(DropdownMenuItem, {
                                                    className: "text-red-600 dark:text-red-400",
                                                    onClick: ()=>setDeleteTarget({
                                                            ids: [
                                                                document1.id
                                                            ],
                                                            label: document1.filename
                                                        }),
                                                    children: [
                                                        /*#__PURE__*/ _jsx(Trash2, {
                                                            className: "w-4 h-4 mr-2"
                                                        }),
                                                        "Delete"
                                                    ]
                                                })
                                            ]
                                        })
                                    ]
                                })
                            ]
                        })
                    })
                })
            ]
        });
    };
    // Specra redesign: standardized inline PageHero + PageBody container.
    const flagsReady = useFeatureFlagsReady();
    const redesignEnabled = useFeatureFlag('specra_redesign_documents');
    const redesign = flagsReady && redesignEnabled;
    const setShowHeader = useLayoutStore((s)=>s.setShowHeader);
    const setShowBreadcrumbs = useLayoutStore((s)=>s.setShowBreadcrumbs);
    useEffect(()=>{
        if (!redesign) return;
        setShowHeader(false);
        setShowBreadcrumbs(false);
        return ()=>{
            setShowHeader(true);
            setShowBreadcrumbs(true);
        };
    }, [
        redesign,
        setShowHeader,
        setShowBreadcrumbs
    ]);
    const content = /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx("div", {
                className: "mb-6",
                children: /*#__PURE__*/ _jsx("div", {
                    className: "border-b border-gray-200 dark:border-gray-700",
                    children: /*#__PURE__*/ _jsxs("nav", {
                        className: "-mb-px flex space-x-8",
                        "aria-label": "Tabs",
                        children: [
                            /*#__PURE__*/ _jsx("button", {
                                onClick: ()=>setActiveTab('documents'),
                                className: `${activeTab === 'documents' ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`,
                                children: "Documents"
                            }),
                            /*#__PURE__*/ _jsx("button", {
                                onClick: ()=>setActiveTab('artifacts'),
                                "data-testid": Testids.artifacts.documentsTab,
                                className: `${activeTab === 'artifacts' ? 'border-indigo-500 text-indigo-600 dark:text-indigo-400' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300 dark:text-gray-400 dark:hover:text-gray-300'} whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm`,
                                children: "Artifacts"
                            })
                        ]
                    })
                })
            }),
            activeTab === 'artifacts' && /*#__PURE__*/ _jsx(ArtifactsView, {}),
            activeTab === 'documents' && /*#__PURE__*/ _jsxs(_Fragment, {
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6 mb-6",
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                className: "flex items-center space-x-2 mb-4",
                                children: [
                                    /*#__PURE__*/ _jsxs("button", {
                                        onClick: ()=>setSearchMode('filename'),
                                        className: `inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${searchMode === 'filename' ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300' : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'}`,
                                        children: [
                                            /*#__PURE__*/ _jsx(Search, {
                                                className: "w-4 h-4 mr-1.5"
                                            }),
                                            "Filename Search"
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs("button", {
                                        onClick: ()=>setSearchMode('semantic'),
                                        className: `inline-flex items-center px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${searchMode === 'semantic' ? 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300' : 'bg-gray-100 text-gray-700 dark:bg-gray-700 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-600'}`,
                                        children: [
                                            /*#__PURE__*/ _jsx(Sparkles, {
                                                className: "w-4 h-4 mr-1.5"
                                            }),
                                            "AI Semantic Search"
                                        ]
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0",
                                children: [
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4",
                                        children: [
                                            searchMode === 'filename' ? /*#__PURE__*/ _jsxs("div", {
                                                className: "relative",
                                                children: [
                                                    /*#__PURE__*/ _jsx(Search, {
                                                        className: "absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4"
                                                    }),
                                                    /*#__PURE__*/ _jsx("input", {
                                                        type: "text",
                                                        placeholder: "Search by filename...",
                                                        value: searchQuery,
                                                        onChange: (e)=>setSearchQuery(e.target.value),
                                                        className: "pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                                    })
                                                ]
                                            }) : /*#__PURE__*/ _jsxs("div", {
                                                className: "relative flex-1 sm:min-w-[400px]",
                                                children: [
                                                    /*#__PURE__*/ _jsx(Sparkles, {
                                                        className: "absolute left-3 top-1/2 transform -translate-y-1/2 text-indigo-500 w-4 h-4"
                                                    }),
                                                    /*#__PURE__*/ _jsx("input", {
                                                        type: "text",
                                                        placeholder: "Search documents by content, meaning, or topic...",
                                                        value: semanticQuery,
                                                        onChange: (e)=>setSemanticQuery(e.target.value),
                                                        onKeyDown: (e)=>{
                                                            if (e.key === 'Enter') {
                                                                handleSemanticSearch();
                                                            }
                                                        },
                                                        className: "pl-10 pr-24 py-2 w-full border border-indigo-300 dark:border-indigo-700 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                                    }),
                                                    /*#__PURE__*/ _jsx("button", {
                                                        onClick: handleSemanticSearch,
                                                        disabled: !semanticQuery.trim() || isSearching,
                                                        className: "absolute right-2 top-1/2 transform -translate-y-1/2 px-3 py-1 bg-indigo-600 text-white text-sm rounded hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed",
                                                        children: isSearching ? /*#__PURE__*/ _jsx(Loader2, {
                                                            className: "w-4 h-4 animate-spin"
                                                        }) : 'Search'
                                                    })
                                                ]
                                            }),
                                            /*#__PURE__*/ _jsxs("select", {
                                                value: filterStatus,
                                                onChange: (e)=>{
                                                    const value = e.target.value;
                                                    if (value === 'all' || value === 'none' || value === 'queued' || value === 'processing' || value === 'ready' || value === 'error') {
                                                        setFilterStatus(value);
                                                    }
                                                },
                                                className: "px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white",
                                                children: [
                                                    /*#__PURE__*/ _jsx("option", {
                                                        value: "all",
                                                        children: "All Status"
                                                    }),
                                                    /*#__PURE__*/ _jsx("option", {
                                                        value: "ready",
                                                        children: "Ready"
                                                    }),
                                                    /*#__PURE__*/ _jsx("option", {
                                                        value: "processing",
                                                        children: "Processing"
                                                    }),
                                                    /*#__PURE__*/ _jsx("option", {
                                                        value: "queued",
                                                        children: "Queued"
                                                    }),
                                                    /*#__PURE__*/ _jsx("option", {
                                                        value: "error",
                                                        children: "Error"
                                                    }),
                                                    /*#__PURE__*/ _jsx("option", {
                                                        value: "none",
                                                        children: "Pending"
                                                    })
                                                ]
                                            }),
                                            /*#__PURE__*/ _jsxs("select", {
                                                value: sortBy,
                                                onChange: (e)=>{
                                                    const value = e.target.value;
                                                    if (value === 'recent' || value === 'name') {
                                                        setSortBy(value);
                                                    }
                                                },
                                                className: "px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white",
                                                children: [
                                                    /*#__PURE__*/ _jsx("option", {
                                                        value: "recent",
                                                        children: "Most Recent"
                                                    }),
                                                    /*#__PURE__*/ _jsx("option", {
                                                        value: "name",
                                                        children: "Name A-Z"
                                                    })
                                                ]
                                            })
                                        ]
                                    }),
                                    /*#__PURE__*/ _jsxs("div", {
                                        className: "flex items-center space-x-4",
                                        children: [
                                            /*#__PURE__*/ _jsx("button", {
                                                onClick: ()=>refetch(),
                                                disabled: loading,
                                                className: "inline-flex items-center px-3 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50",
                                                title: "Refresh",
                                                children: /*#__PURE__*/ _jsx(RefreshCw, {
                                                    className: `w-4 h-4 ${loading ? 'animate-spin' : ''}`
                                                })
                                            }),
                                            /*#__PURE__*/ _jsxs(Link, {
                                                to: "/documents/upload",
                                                className: "inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700",
                                                children: [
                                                    /*#__PURE__*/ _jsx(Upload, {
                                                        className: "w-4 h-4 mr-2"
                                                    }),
                                                    "Upload"
                                                ]
                                            }),
                                            selectedDocuments.length > 0 && /*#__PURE__*/ _jsxs("div", {
                                                className: "flex items-center space-x-2",
                                                children: [
                                                    /*#__PURE__*/ _jsxs("span", {
                                                        className: "text-sm text-gray-600 dark:text-gray-400",
                                                        children: [
                                                            selectedDocuments.length,
                                                            " selected"
                                                        ]
                                                    }),
                                                    /*#__PURE__*/ _jsxs("button", {
                                                        onClick: ()=>setDeleteTarget({
                                                                ids: selectedDocuments,
                                                                label: `${selectedDocuments.length} document(s)`
                                                            }),
                                                        className: "inline-flex items-center px-3 py-1 border border-transparent text-sm font-medium rounded text-red-600 bg-red-100 hover:bg-red-200 dark:bg-red-900/20 dark:hover:bg-red-900/30",
                                                        children: [
                                                            /*#__PURE__*/ _jsx(Trash2, {
                                                                className: "w-4 h-4 mr-1"
                                                            }),
                                                            "Delete"
                                                        ]
                                                    })
                                                ]
                                            })
                                        ]
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                className: "mt-4 flex items-center space-x-4 text-sm text-gray-600 dark:text-gray-400",
                                children: searchMode === 'filename' ? /*#__PURE__*/ _jsxs(_Fragment, {
                                    children: [
                                        /*#__PURE__*/ _jsxs("span", {
                                            children: [
                                                "Total: ",
                                                /*#__PURE__*/ _jsx("strong", {
                                                    className: "text-gray-900 dark:text-white",
                                                    children: total
                                                })
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsxs("span", {
                                            children: [
                                                "Showing:",
                                                ' ',
                                                /*#__PURE__*/ _jsx("strong", {
                                                    className: "text-gray-900 dark:text-white",
                                                    children: filteredDocuments.length
                                                })
                                            ]
                                        })
                                    ]
                                }) : semanticResults && /*#__PURE__*/ _jsxs(_Fragment, {
                                    children: [
                                        /*#__PURE__*/ _jsxs("span", {
                                            children: [
                                                "Results:",
                                                ' ',
                                                /*#__PURE__*/ _jsx("strong", {
                                                    className: "text-gray-900 dark:text-white",
                                                    children: semanticResults.total
                                                })
                                            ]
                                        }),
                                        semanticResults.filtered_by_permissions > 0 && /*#__PURE__*/ _jsxs("span", {
                                            className: "text-yellow-600 dark:text-yellow-400",
                                            children: [
                                                "(",
                                                semanticResults.filtered_by_permissions,
                                                " filtered by permissions)"
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsxs("span", {
                                            children: [
                                                "Query time:",
                                                ' ',
                                                /*#__PURE__*/ _jsxs("strong", {
                                                    className: "text-gray-900 dark:text-white",
                                                    children: [
                                                        semanticResults.processing_time_ms,
                                                        "ms"
                                                    ]
                                                })
                                            ]
                                        }),
                                        semanticResults.cache_hit && /*#__PURE__*/ _jsx("span", {
                                            className: "text-green-600 dark:text-green-400",
                                            children: "(cached)"
                                        })
                                    ]
                                })
                            })
                        ]
                    }),
                    searchError && /*#__PURE__*/ _jsxs("div", {
                        className: "bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6",
                        children: [
                            /*#__PURE__*/ _jsx("h3", {
                                className: "font-medium text-red-900 dark:text-red-200",
                                children: "Search error"
                            }),
                            /*#__PURE__*/ _jsx("p", {
                                className: "text-sm text-red-700 dark:text-red-300 mt-1",
                                children: searchError instanceof Error ? searchError.message : 'Failed to perform semantic search'
                            })
                        ]
                    }),
                    (searchMode === 'filename' && loading || searchMode === 'semantic' && isSearching) && filteredDocuments.length === 0 && /*#__PURE__*/ _jsxs("div", {
                        className: "text-center py-12",
                        children: [
                            /*#__PURE__*/ _jsx(Loader2, {
                                className: "mx-auto h-12 w-12 text-indigo-600 animate-spin"
                            }),
                            /*#__PURE__*/ _jsx("h3", {
                                className: "mt-2 text-sm font-medium text-gray-900 dark:text-white",
                                children: searchMode === 'semantic' ? 'Searching documents...' : 'Loading documents...'
                            })
                        ]
                    }),
                    fetchError && /*#__PURE__*/ _jsxs("div", {
                        className: "bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 mb-6",
                        children: [
                            /*#__PURE__*/ _jsx("h3", {
                                className: "font-medium text-red-900 dark:text-red-200",
                                children: "Error loading documents"
                            }),
                            /*#__PURE__*/ _jsx("p", {
                                className: "text-sm text-red-700 dark:text-red-300 mt-1",
                                children: fetchError
                            })
                        ]
                    }),
                    !(searchMode === 'filename' && loading || searchMode === 'semantic' && isSearching) && filteredDocuments.length > 0 && (viewMode === 'grid' ? /*#__PURE__*/ _jsx("div", {
                        className: "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6",
                        children: filteredDocuments.map((document1)=>/*#__PURE__*/ _jsx(DocumentCard, {
                                document: document1
                            }, document1.id))
                    }) : /*#__PURE__*/ _jsx("div", {
                        className: "bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden",
                        children: /*#__PURE__*/ _jsx("div", {
                            className: "overflow-x-auto",
                            children: /*#__PURE__*/ _jsxs("table", {
                                className: "min-w-full divide-y divide-gray-200 dark:divide-gray-700",
                                children: [
                                    /*#__PURE__*/ _jsx("thead", {
                                        className: "bg-gray-50 dark:bg-gray-900/50",
                                        children: /*#__PURE__*/ _jsxs("tr", {
                                            children: [
                                                /*#__PURE__*/ _jsx("th", {
                                                    className: "px-6 py-3 text-left",
                                                    children: /*#__PURE__*/ _jsx("input", {
                                                        type: "checkbox",
                                                        checked: selectedDocuments.length === filteredDocuments.length && filteredDocuments.length > 0,
                                                        onChange: handleSelectAll,
                                                        className: "rounded border-gray-300 text-indigo-600 shadow-sm focus:border-indigo-300 focus:ring focus:ring-indigo-200 focus:ring-opacity-50"
                                                    })
                                                }),
                                                /*#__PURE__*/ _jsxs("th", {
                                                    className: "px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider",
                                                    children: [
                                                        "Document",
                                                        searchMode === 'semantic' && /*#__PURE__*/ _jsx("span", {
                                                            className: "ml-2 text-xs font-normal text-indigo-600 dark:text-indigo-400",
                                                            children: "(by relevance)"
                                                        })
                                                    ]
                                                }),
                                                searchMode === 'semantic' && /*#__PURE__*/ _jsx("th", {
                                                    className: "px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider",
                                                    children: "Matched Content"
                                                }),
                                                searchMode === 'filename' && /*#__PURE__*/ _jsx("th", {
                                                    className: "px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider",
                                                    children: "Project"
                                                }),
                                                /*#__PURE__*/ _jsx("th", {
                                                    className: "px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider",
                                                    children: searchMode === 'semantic' ? 'Relevance' : 'Status'
                                                }),
                                                /*#__PURE__*/ _jsx("th", {
                                                    className: "px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider",
                                                    children: searchMode === 'semantic' ? 'Source' : 'Upload Date'
                                                }),
                                                /*#__PURE__*/ _jsx("th", {
                                                    className: "px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider",
                                                    children: searchMode === 'semantic' ? 'Type' : 'Job ID'
                                                }),
                                                /*#__PURE__*/ _jsx("th", {
                                                    className: "px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider",
                                                    children: "Actions"
                                                })
                                            ]
                                        })
                                    }),
                                    /*#__PURE__*/ _jsx("tbody", {
                                        className: "bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700",
                                        children: filteredDocuments.map((document1)=>/*#__PURE__*/ _jsx(DocumentRow, {
                                                document: document1
                                            }, document1.id))
                                    })
                                ]
                            })
                        })
                    })),
                    !(searchMode === 'filename' && loading || searchMode === 'semantic' && isSearching) && filteredDocuments.length === 0 && !fetchError && !searchError && /*#__PURE__*/ _jsx("div", {
                        className: "text-center py-12",
                        children: searchMode === 'semantic' ? /*#__PURE__*/ _jsxs(_Fragment, {
                            children: [
                                /*#__PURE__*/ _jsx(Sparkles, {
                                    className: "mx-auto h-12 w-12 text-indigo-400"
                                }),
                                /*#__PURE__*/ _jsx("h3", {
                                    className: "mt-2 text-sm font-medium text-gray-900 dark:text-white",
                                    children: "No matching documents found"
                                }),
                                /*#__PURE__*/ _jsx("p", {
                                    className: "mt-1 text-sm text-gray-500 dark:text-gray-400",
                                    children: "Try a different search query or switch to filename search."
                                })
                            ]
                        }) : /*#__PURE__*/ _jsxs(_Fragment, {
                            children: [
                                /*#__PURE__*/ _jsx(FileText, {
                                    className: "mx-auto h-12 w-12 text-gray-400"
                                }),
                                /*#__PURE__*/ _jsx("h3", {
                                    className: "mt-2 text-sm font-medium text-gray-900 dark:text-white",
                                    children: "No documents found"
                                }),
                                /*#__PURE__*/ _jsx("p", {
                                    className: "mt-1 text-sm text-gray-500 dark:text-gray-400",
                                    children: searchQuery || filterStatus !== 'all' ? 'Try adjusting your search or filter criteria.' : 'Get started by uploading your first document.'
                                }),
                                !searchQuery && filterStatus === 'all' && /*#__PURE__*/ _jsx("div", {
                                    className: "mt-6",
                                    children: /*#__PURE__*/ _jsxs(Link, {
                                        to: "/documents/upload",
                                        className: "inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-indigo-600 hover:bg-indigo-700",
                                        children: [
                                            /*#__PURE__*/ _jsx(Upload, {
                                                className: "w-4 h-4 mr-2"
                                            }),
                                            "Upload Document"
                                        ]
                                    })
                                })
                            ]
                        })
                    }),
                    previewDocument && /*#__PURE__*/ _jsx(DocumentPreviewModal, {
                        document: previewDocument,
                        onClose: ()=>setPreviewDocument(null)
                    }),
                    /*#__PURE__*/ _jsx(Dialog, {
                        open: !!deleteTarget,
                        onOpenChange: (open)=>!open && setDeleteTarget(null),
                        children: /*#__PURE__*/ _jsxs(DialogContent, {
                            children: [
                                /*#__PURE__*/ _jsxs(DialogHeader, {
                                    children: [
                                        /*#__PURE__*/ _jsxs(DialogTitle, {
                                            children: [
                                                "Delete Document",
                                                deleteTarget && deleteTarget.ids.length > 1 ? 's' : ''
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsxs(DialogDescription, {
                                            children: [
                                                "Are you sure you want to delete",
                                                ' ',
                                                /*#__PURE__*/ _jsx("span", {
                                                    className: "font-medium",
                                                    children: deleteTarget?.label
                                                }),
                                                "? This action cannot be undone."
                                            ]
                                        })
                                    ]
                                }),
                                /*#__PURE__*/ _jsxs(DialogFooter, {
                                    children: [
                                        /*#__PURE__*/ _jsx(Button, {
                                            variant: "outline",
                                            onClick: ()=>setDeleteTarget(null),
                                            disabled: isDeleting,
                                            children: "Cancel"
                                        }),
                                        /*#__PURE__*/ _jsx(Button, {
                                            variant: "destructive",
                                            onClick: handleDeleteConfirm,
                                            disabled: isDeleting,
                                            children: isDeleting ? 'Deleting...' : 'Delete'
                                        })
                                    ]
                                })
                            ]
                        })
                    })
                ]
            })
        ]
    });
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsxs(Helmet, {
                children: [
                    /*#__PURE__*/ _jsx("title", {
                        children: "Documents - GovCon AI"
                    }),
                    /*#__PURE__*/ _jsx("meta", {
                        name: "description",
                        content: "Document Management System for Government Contracting"
                    })
                ]
            }),
            redesign ? /*#__PURE__*/ _jsxs(PageBody, {
                children: [
                    /*#__PURE__*/ _jsx(PageHero, {
                        title: "Documents",
                        subtitle: "Manage your documents and generated artifacts."
                    }),
                    content
                ]
            }) : /*#__PURE__*/ _jsx("div", {
                className: "p-6",
                children: content
            }),
            /*#__PURE__*/ _jsx(ArtifactEditorDrawer, {
                isOpen: isOpen,
                onClose: closeDrawer
            }),
            /*#__PURE__*/ _jsx(OfficeDocumentDrawer, {
                overlay: true
            })
        ]
    });
}
export default function Documents() {
    return /*#__PURE__*/ _jsx(OfficeDocumentDrawerProvider, {
        children: /*#__PURE__*/ _jsx(ArtifactDrawerProvider, {
            children: /*#__PURE__*/ _jsx(DocumentsContent, {})
        })
    });
}
